import { useQuery } from '@tanstack/react-query';
import { listMyApplications, type ApiDistributorApplication } from '@/api/vendo';
import useAuthStore from '@/store/authStore';

/**
 * Source of truth for whether the current user is an approved SmartFi
 * Distributor. Derived from `listMyApplications`:
 *
 *   - active partner: at least one approved application with `partnerCreated`
 *     populated (the admin approval flow creates the DistributorPartner row
 *     and writes its id back).
 *   - pending: at least one application in 'pending' status.
 *
 * Skipped when no user is logged in so the query doesn't fire on public
 * routes. The query has no staleTime override — it inherits the global
 * QueryClient defaults (refetchOnWindowFocus: true), so an admin approval
 * landing in another tab will reflect on focus return.
 */
export interface DistributorStatus {
   isDistributor: boolean;
   pendingApplication: ApiDistributorApplication | undefined;
   approvedApplication: ApiDistributorApplication | undefined;
   applications: ApiDistributorApplication[];
   isLoading: boolean;
}

export function useDistributorStatus(): DistributorStatus {
   const isAuthed = useAuthStore((s) => s.isAuthenticated);
   const { data: applications = [], isLoading } = useQuery({
      queryKey: ['vendo', 'distributor-applications', 'mine'],
      queryFn: listMyApplications,
      enabled: isAuthed,
   });
   // partnerActive === false: the admin switched the shop off — no Distributors menu / badge (Ray 2026-10-27).
   // An older server sends no partnerActive: treat it as on.
   const approvedApplication = applications.find(
      (a) => a.status === 'approved' && !!a.partnerCreated && a.partnerActive !== false
   );
   const pendingApplication = applications.find((a) => a.status === 'pending');
   return {
      isDistributor: !!approvedApplication,
      pendingApplication,
      approvedApplication,
      applications,
      isLoading,
   };
}
